import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/storybook/components/Toast/Toast';

const COPY_FEEDBACK_DURATION_MS = 1000;

interface UseCopyToClipboardOptions {
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  resetAfterMs?: number | null;
}

interface UseCopyToClipboardResult {
  isCopied: boolean;
  copy: (text: string) => Promise<void>;
  reset: VoidFunction;
}

export const useCopyToClipboard = ({
  showSuccessToast = false,
  showErrorToast = false,
  resetAfterMs = COPY_FEEDBACK_DURATION_MS,
}: UseCopyToClipboardOptions = {}): UseCopyToClipboardResult => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied || resetAfterMs === null) {
      return undefined;
    }

    const timer = window.setTimeout(() => setIsCopied(false), resetAfterMs);

    return () => window.clearTimeout(timer);
  }, [isCopied, resetAfterMs]);

  const handleCopyError = useCallback(() => {
    setIsCopied(false);
    if (showErrorToast) {
      makeToast({
        content: String(t('global.copyFailed')),
        appearance: 'error',
      });
    }
  }, [makeToast, t, showErrorToast]);

  const handleCopySuccess = useCallback(() => {
    if (showSuccessToast) {
      makeToast({
        content: String(t('global.copiedToClipboard')),
        appearance: 'success',
      });
    }
  }, [makeToast, t, showSuccessToast]);

  const copy = useCallback(
    (text: string): Promise<void> => {
      if (!text) {
        return Promise.resolve();
      }

      if (!navigator.clipboard) {
        handleCopyError();
        return Promise.resolve();
      }

      setIsCopied(true);

      return navigator.clipboard.writeText(text).then(handleCopySuccess, handleCopyError);
    },
    [handleCopySuccess, handleCopyError],
  );

  const reset = useCallback(() => {
    setIsCopied(false);
  }, []);

  return { isCopied, copy, reset };
};
